/* eslint-disable  @typescript-eslint/no-explicit-any */

import { AnyAction, AsyncThunk, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ConsoleLogger, type AppDispatch } from '@nextgen-web-framework/core';
import { transformOrderHistoryData } from '../../transformers/orderHistoryTransformer';
import type { InitialOrderState, IOrderHistory, IOrderHistorySliceRequest } from '../../types';
import type { OrderHistoryResponse } from '../models';

import { postPurchaseApi } from '../requests/postpurchaseApi';

type GenericAsyncThunk = AsyncThunk<
  OrderHistoryResponse,
  unknown,
  {
    // Optional fields for defining thunkApi field types
    dispatch: any; // this is store.dispatch
    state: InitialOrderState;
    extra: {
      // eslint-disable-next-line
      CART_BASE_API_URL: string;
    };
  }
>;
type PendingAction = ReturnType<GenericAsyncThunk['pending']>;
type RejectedAction = ReturnType<GenericAsyncThunk['rejected']>;
type FulfilledAction = ReturnType<GenericAsyncThunk['fulfilled']>;

function isPendingGenericAction(action: AnyAction): action is PendingAction {
  return action.type.endsWith('/pending');
}
function isFulfilledAction(action: AnyAction): action is FulfilledAction {
  return action.type.endsWith('/fulfilled');
}

function isRejectedAction(action: AnyAction): action is RejectedAction {
  return action.type.endsWith('/rejected');
}

export const getOrdersHistorySliceReq = createAsyncThunk<
  IOrderHistory | null,
  IOrderHistorySliceRequest,
  {
    // Optional fields for defining thunkApi field types
    dispatch: AppDispatch; // this is store.dispatch
    state: InitialOrderState;
    extra: {
      loggedInUserId: string;
    };
  }
>('commerceOrder/getOrderHistory', async ({ ...rest }: IOrderHistorySliceRequest) => {
  const res: any = await postPurchaseApi.getOrderHistory({
    ...rest.queryData,
  });
  try {
    return transformOrderHistoryData(res, rest.loggedInAccountId);
  } catch (e) {
    ConsoleLogger.log(e);
    return null;
  }
});

const initialState: InitialOrderState = {
  isLoading: false,
  hasError: false,
  error: {},
  orders: [],
  limit: 0,
  offset: 0,
  total: 0, //total pages
  filter: {},
  totalRecords: 0,
  keyword: '',
};

export const orderHistorySlice = createSlice({
  name: 'orderHistory',
  initialState,
  reducers: {
    resetOrderHistorySlice: () => initialState,
    updateOffset: (state, action) => {
      state.offset = action.payload;
    },
    updateLimit: (state, action) => {
      state.limit = action.payload;
    },
    updateFilter: (state, action) => {
      state.filter = action.payload;
    },
    updateOrder: (state, action) => {
      state.orders = action.payload;
    },
    updateKeywordAndOrders: (state, action) => {
      if (action.payload.keyword !== state.keyword) {
        return { ...state, ...action.payload };
      }
      return state;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOrdersHistorySliceReq.fulfilled, (state, action) => {
      if (action?.payload) {
        if (state.offset < action?.payload.total) {
          state.offset++;
        }
        state.orders = [...state.orders, ...action.payload.orders] as [];
        state.total = action.payload.total;
        state.totalRecords = action.payload.totalRecords;
      }
    });

    /**
     * Common reducers
     */
    builder.addMatcher(isRejectedAction, (state: InitialOrderState, action: any) => {
      state.isLoading = false;
      state.hasError = true;
      state.error = action.payload;
    });
    builder.addMatcher(isFulfilledAction, (state: InitialOrderState) => {
      state.isLoading = false;
      state.hasError = false;
      // ConsoleLogger.log('data found, add handlers', state);
    });
    builder.addMatcher(isPendingGenericAction, (state: InitialOrderState) => {
      state.isLoading = true;
      state.hasError = false;
    });
  },
});

export const { resetOrderHistorySlice, updateLimit, updateOffset, updateFilter, updateOrder, updateKeywordAndOrders } =
  orderHistorySlice.actions;
